import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { user, login, loginWithGoogle, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/research" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/research");
  };

  const onGoogle = async () => {
    setGoogleLoading(true);
    const ok = await loginWithGoogle();
    if (!ok) setGoogleLoading(false); // on success the browser redirects away
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2 bg-background text-foreground">
      {/* Brand panel */}
      <div className="relative hidden md:flex flex-col justify-between overflow-hidden bg-surface border-r border-border p-12">
        {/* A faint violet glow — the website's atmosphere, toned down for the app. */}
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full"
          style={{ background: "radial-gradient(closest-side, hsl(var(--primary) / 0.16), transparent)" }} />
        <Link to="/" className="relative w-fit" aria-label="Inbound Labs home">
          <Logo size={32} textClass="text-[22px]" />
        </Link>
        <div className="max-w-md">
          <p className="relative font-display text-[32px] leading-[1.15] tracking-[-0.03em]">
            “Research, enrich, and reach the right accounts —
            <span className="text-violet-text"> without the noise.</span>”
          </p>
          <p className="relative mt-4 text-[15px] leading-6 text-ink-secondary">
            A calm workspace for go-to-market teams who actually read their pipeline.
          </p>
        </div>
        <div className="relative text-xs text-muted-foreground">© {new Date().getFullYear()} Inbound Labs</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="w-full max-w-sm space-y-6"
        >
          <div>
            <h1 className="font-display text-[32px] leading-tight tracking-[-0.02em]">Welcome back</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Sign in to your InboundLabs workspace.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-[13px] font-medium text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                data-testid="login-email-input"
                className="mt-1"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[13px] font-medium text-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                data-testid="login-password-input"
                className="mt-1"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[13px] font-medium text-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={onGoogle}
            disabled={googleLoading}
            data-testid="login-google-button"
            className="w-full gap-2"
          >
            {googleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
              </svg>
            )}
            {googleLoading ? "Redirecting…" : "Continue with Google"}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            New to InboundLabs?{" "}
            <Link to="/register" className="text-foreground underline underline-offset-4" data-testid="goto-register-link">
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
